import React, { useState } from 'react';
import { Head, Section } from '@/components';
import { ARTICLES } from '@/data/constants';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

/**
 * Insights (Blog) listing page.
 * Features a high-tech/premium grid layout of articles.
 */
const Insights: React.FC = () => {
  const [filter, setFilter] = useState('All');
  const categories = ['All', ...new Set(ARTICLES.map(article => article.category))];

  const filteredArticles = filter === 'All' 
    ? ARTICLES 
    : ARTICLES.filter(article => article.category === filter);

  return (
    <>
      <Head 
        title="Insights | Kivosystems"
        description="Thought leadership on web performance, software architecture, and digital product design."
        canonicalUrl="https://www.kivosystems.com/insights"
        url="https://www.kivosystems.com/insights"
        image="/logo.svg"
        keywords="web performance, software architecture, digital product design, internal tools, ai automation, conversion optimization"
      />

      {/* Hero */}
      <Section className="bg-obsidian pt-32 pb-16">
        <div className="max-w-4xl">
          <h1 className="text-5xl md:text-7xl font-display font-medium text-white mb-6">
            Digital <span className="text-sapphire-500">Intel.</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl leading-relaxed">
            Exploring the intersection of design, technology, and business performance.
          </p>
        </div>

        {/* Filter Bar */}
        <div className="mt-16 flex flex-wrap gap-4 items-center border-b border-white/10 pb-8">
            {categories.map(cat => (
                <button
                    key={cat}
                    onClick={() => setFilter(cat)}
                    className={`px-6 py-2 rounded-full text-sm font-bold uppercase tracking-wider transition-all border ${
                        filter === cat 
                        ? 'bg-sapphire-500 border-sapphire-500 text-white' 
                        : 'bg-transparent border-white/10 text-gray-400 hover:border-white/30 hover:text-white'
                    }`}
                >
                    {cat}
                </button>
            ))}
        </div>
      </Section>

      {/* Grid */}
      <Section className="bg-obsidian pt-0 pb-32">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map((article) => (
                <Link to={`/insights/${article.slug}`} key={article.id} className="group relative block h-full">
                    <div className="absolute inset-0 bg-sapphire-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl blur-xl" />
                    <article className="relative h-full bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-sapphire-500/50 transition-colors duration-300 flex flex-col">
                        <div className="aspect-video overflow-hidden">
                            <img 
                                src={article.image} 
                                alt={article.title}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                loading="lazy"
                            />
                        </div>
                        <div className="p-8 flex-1 flex flex-col">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-xs font-bold uppercase tracking-widest text-sapphire-500">{article.category}</span>
                                <span className="text-xs text-gray-500 font-mono">{article.date}</span>
                            </div>
                            <h2 className="text-2xl font-bold text-white mb-4 leading-tight group-hover:text-sapphire-400 transition-colors">
                                {article.title}
                            </h2>
                            <p className="text-gray-400 text-sm leading-relaxed mb-8 line-clamp-3 flex-1">
                                {article.excerpt}
                            </p>
                            <div className="flex items-center gap-2 text-sm font-bold text-white mt-auto">
                                <span>Read Article</span>
                                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                            </div>
                        </div>
                    </article>
                </Link>
            ))}
        </div>
      </Section>
    </>
  );
};

export default Insights;
